(() => {
  const debugEnabled = new URLSearchParams(window.location.search).get('zhibaiGiftDebug') === '1';
  const debug = (label, details) => {
    if (debugEnabled) console.info(`[ZHIBAI Gift] ${label}`, details);
  };
  const configElement = document.getElementById('ZhibaiFreeGiftConfig');
  if (!configElement) {
    debug('skip reason', 'Config element was not found.');
    return;
  }

  let config;

  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    console.warn('[ZHIBAI free gift] Invalid Liquid configuration.', error);
    return;
  }

  const giftVariantId = Number(config.giftVariantId) || null;
  const giftProductId = config.giftProductId ? String(config.giftProductId) : '';
  const eligibleCollectionHandles = Array.isArray(config.eligibleCollectionHandles)
    ? config.eligibleCollectionHandles.map(String).filter(Boolean)
    : [];
  const eligibleProductIds = new Set(
    Array.isArray(config.eligibleProductIds) ? config.eligibleProductIds.map(String).filter(Boolean) : []
  );

  debug('config', {
    giftVariantId,
    giftProductId,
    giftProductHandle: config.giftProductHandle || '',
    giftVariantAvailable: config.giftVariantAvailable,
    eligibleCollectionHandles,
    eligibleProductIds: [...eligibleProductIds],
  });

  if (!giftProductId) {
    debug('skip reason', 'Gift product could not be resolved by Liquid.');
    return;
  }

  const CAMPAIGN = 'tea-towel-2026';
  const GIFT_PROPERTY = '_zhibai_free_gift';
  const CAMPAIGN_PROPERTY = '_gift_campaign';
  const DEBOUNCE_DELAY = 80;
  const MAX_RECONCILIATION_PASSES = 6;
  const routeConfig = window.routes || {};
  const cartUrl = routeConfig.cart_url || '/cart';
  const cartJsonUrl = cartUrl.endsWith('.js') ? cartUrl : `${cartUrl}.js`;
  const cartAddUrl = routeConfig.cart_add_url || '/cart/add.js';
  const cartChangeUrl = routeConfig.cart_change_url || '/cart/change.js';
  const shopRoot = window.Shopify?.routes?.root || '/';
  const normalizedShopRoot = shopRoot.endsWith('/') ? shopRoot : `${shopRoot}/`;

  let debounceTimer;
  let activeReconciliation = null;
  let eligibleProductsLoad = null;
  let rerunRequested = false;
  let giftAddBlocked = false;
  let checkoutPending = false;
  let allowCheckoutSubmit = false;

  const getProperty = (item, propertyName) => item?.properties?.[propertyName];

  const isManagedGift = (item) =>
    String(item?.product_id) === giftProductId &&
    String(getProperty(item, GIFT_PROPERTY)) === 'true' &&
    String(getProperty(item, CAMPAIGN_PROPERTY)) === CAMPAIGN;

  const isQualifyingLine = (item) =>
    Number(item?.quantity) > 0 && eligibleProductIds.has(String(item?.product_id)) && !isManagedGift(item);

  async function requestJson(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {}),
      },
    });
    const payload = await response.json().catch(() => null);

    if (!response.ok || payload?.status >= 400) {
      const error = new Error(payload?.description || payload?.message || `Cart request failed (${response.status}).`);
      error.status = payload?.status || response.status;
      throw error;
    }

    return payload;
  }

  async function ensureEligibleProductIds() {
    if (eligibleProductIds.size > 0 || eligibleCollectionHandles.length === 0) return;
    if (eligibleProductsLoad) return eligibleProductsLoad;

    eligibleProductsLoad = Promise.allSettled(
      eligibleCollectionHandles.map((handle) =>
        requestJson(`${normalizedShopRoot}collections/${encodeURIComponent(handle)}/products.json?limit=250`, {
          cache: 'no-store',
        })
      )
    ).then((results) => {
      results.forEach((result) => {
        if (result.status !== 'fulfilled' || !Array.isArray(result.value?.products)) return;

        result.value.products.forEach((product) => {
          if (product?.id) eligibleProductIds.add(String(product.id));
        });
      });

      debug('eligible', {
        source: 'collection JSON fallback',
        eligibleProductIds: [...eligibleProductIds],
      });
    });

    return eligibleProductsLoad;
  }

  function fetchCart() {
    return requestJson(cartJsonUrl, { cache: 'no-store' });
  }

  function changeManagedGiftLine(lineKey, quantity) {
    return requestJson(cartChangeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id: lineKey, quantity }),
    });
  }

  async function addManagedGift() {
    if (!giftVariantId) {
      debug('skip reason', 'Gift variant ID is empty.');
      return false;
    }

    if (giftAddBlocked) {
      debug('skip reason', 'Gift add is blocked after an earlier failed add request.');
      return false;
    }

    try {
      debug('adding gift', { giftVariantId });
      await requestJson(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [
            {
              id: giftVariantId,
              quantity: 1,
              properties: {
                [GIFT_PROPERTY]: 'true',
                [CAMPAIGN_PROPERTY]: CAMPAIGN,
              },
            },
          ],
        }),
      });
      return true;
    } catch (error) {
      giftAddBlocked = true;
      console.warn('[ZHIBAI free gift] Gift could not be added; normal cart actions remain available.', error);
      return false;
    }
  }

  async function reconcileOnce() {
    await ensureEligibleProductIds();

    if (eligibleProductIds.size === 0) {
      debug('skip reason', 'No eligible product IDs were returned by Liquid or the collection JSON fallback.');
      return false;
    }

    const cart = await fetchCart();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const managedGifts = items.filter(isManagedGift);
    const hasQualifyingProduct = items.some(isQualifyingLine);

    debug('cart', cart);
    debug('eligible', {
      hasQualifyingProduct,
      qualifyingProductIds: items.filter(isQualifyingLine).map((item) => String(item.product_id)),
      eligibleProductIds: [...eligibleProductIds],
    });

    if (!hasQualifyingProduct) {
      giftAddBlocked = false;
      let changed = false;

      if (managedGifts.length === 0) {
        debug('skip reason', 'Cart does not contain a qualifying product or a managed gift.');
      }

      for (const gift of managedGifts) {
        if (!gift.key) continue;

        try {
          debug('removing gift', { lineKey: gift.key });
          await changeManagedGiftLine(gift.key, 0);
          changed = true;
        } catch (error) {
          console.warn('[ZHIBAI free gift] Automatic gift could not be removed.', error);
        }
      }

      return changed;
    }

    if (managedGifts.length === 0) {
      return addManagedGift();
    }

    giftAddBlocked = false;
    let changed = false;
    const [giftToKeep, ...duplicateGifts] = managedGifts;

    if (giftToKeep.key && Number(giftToKeep.quantity) !== 1) {
      try {
        debug('removing gift', { reason: 'normalizing quantity', lineKey: giftToKeep.key });
        await changeManagedGiftLine(giftToKeep.key, 1);
        changed = true;
      } catch (error) {
        console.warn('[ZHIBAI free gift] Automatic gift quantity could not be corrected.', error);
      }
    }

    for (const duplicateGift of duplicateGifts) {
      if (!duplicateGift.key) continue;

      try {
        debug('removing gift', { reason: 'duplicate', lineKey: duplicateGift.key });
        await changeManagedGiftLine(duplicateGift.key, 0);
        changed = true;
      } catch (error) {
        console.warn('[ZHIBAI free gift] A duplicate automatic gift could not be removed.', error);
      }
    }

    if (!changed) {
      debug('skip reason', 'One managed gift is already present with quantity 1.');
    }

    return changed;
  }

  async function refreshCartUI() {
    if (typeof refreshDawnCartUI === 'function') {
      await refreshDawnCartUI();
      return;
    }

    const cartData = await fetchCart().catch(() => null);
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      await publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'zhibai-free-gift',
        cartData: cartData || undefined,
      });
    }
  }

  function runReconciliation() {
    if (activeReconciliation) {
      rerunRequested = true;
      return activeReconciliation;
    }

    activeReconciliation = (async () => {
      let pass = 0;
      let cartChanged = false;

      do {
        rerunRequested = false;
        cartChanged = await reconcileOnce();

        if (cartChanged) {
          await refreshCartUI();
        }

        pass += 1;
      } while ((cartChanged || rerunRequested) && pass < MAX_RECONCILIATION_PASSES);
    })()
      .catch((error) => {
        console.warn('[ZHIBAI free gift] Reconciliation stopped safely.', error);
      })
      .finally(() => {
        activeReconciliation = null;

        if (rerunRequested) {
          scheduleReconciliation(0);
        }
      });

    return activeReconciliation;
  }

  function scheduleReconciliation(delay = DEBOUNCE_DELAY) {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      debounceTimer = undefined;
      runReconciliation();
    }, delay);
  }

  function reconcileBeforeCheckout(event) {
    const form = event.target;
    const submitter = event.submitter;

    if (!(form instanceof HTMLFormElement) || submitter?.name !== 'checkout') return;

    if (allowCheckoutSubmit) {
      allowCheckoutSubmit = false;
      return;
    }

    event.preventDefault();

    if (checkoutPending) return;
    checkoutPending = true;
    window.clearTimeout(debounceTimer);

    runReconciliation().finally(() => {
      checkoutPending = false;
      allowCheckoutSubmit = true;

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter);
      } else {
        form.submit();
      }
    });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      debug('cart update event', event?.source || 'unknown');
      scheduleReconciliation(event?.source === 'product-form' ? 0 : DEBOUNCE_DELAY);
    });
  } else {
    debug('skip reason', 'Craft cart pub/sub was unavailable; initial and pageshow reconciliation remain active.');
  }

  document.addEventListener('submit', reconcileBeforeCheckout, true);
  window.addEventListener('pageshow', () => scheduleReconciliation(0));

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        debug('initial reconcile', 'DOMContentLoaded');
        scheduleReconciliation(0);
      },
      { once: true }
    );
  } else {
    debug('initial reconcile', document.readyState);
    scheduleReconciliation(0);
  }
})();